import { useState } from 'react';
import { get } from 'lodash';

import { FIELD_PATHS, FIELD_VALUES, YES_NO_OPTIONS } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { ApplicantEntityIndex, RelatedParty, StoreApplicant } from '@funnels/types';
import { HelpTooltip } from '@funnels/ui-components/_common';
import { initialFormValues } from '@funnels/utils';

import UncontrolledDropdownField from '../../Fields/_uncontrolled/DropdownField';
import UncontrolledRadioButtonsField from '../../Fields/_uncontrolled/RadioButtonsField';

interface DirectorshipProp extends ApplicantEntityIndex {}

export const Directorship = ({ applicantIndex }: DirectorshipProp) => {
  const [formValues, setFormValues] = useFormValues();
  const applicants: StoreApplicant[] = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const companyApplicants = applicants.filter(
    (_applicant, index) => get({ applicants }, FIELD_PATHS.APPLICANT_ENTITY(index)) === FIELD_VALUES.COMPANY
  );

  const companyApplicantsOptions = companyApplicants.map((_applicant, index) => ({
    label: get({ applicants: companyApplicants }, FIELD_PATHS.APPLICANT_COMPANY_NAME(index), `Company ${index + 1}`),
    value: get({ applicants: companyApplicants }, FIELD_PATHS.APPLICANT_CLIENT_ID(index)),
  }));
  const applicantClientId = get(formValues, FIELD_PATHS.APPLICANT_CLIENT_ID(applicantIndex));

  const relatedParties: RelatedParty[] = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []);
  const otherRelatedParties = relatedParties.filter(
    (_, index) =>
      get(formValues, FIELD_PATHS.RELATED_PARTY_TYPE(index)) !== FIELD_VALUES.DIRECTOR ||
      get(formValues, FIELD_PATHS.RELATED_PARTY_APPLICANT_CLIENT_ID(index)) !== applicantClientId
  );
  const relatedDirectors = relatedParties.filter(
    (_, index) =>
      get(formValues, FIELD_PATHS.RELATED_PARTY_TYPE(index)) === FIELD_VALUES.DIRECTOR &&
      get(formValues, FIELD_PATHS.RELATED_PARTY_APPLICANT_CLIENT_ID(index)) === applicantClientId
  );

  const [isDirector, setIsDirector] = useState(relatedDirectors.length > 0);
  const handleDirectorshipUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === FIELD_VALUES.TRUE) {
      setIsDirector(true);
      if (companyApplicants.length === 1 && companyApplicants[0].clientId) {
        setFormValues(FIELD_PATHS.RELATED_PARTIES(), [
          ...otherRelatedParties,
          initialFormValues.INITIAL_DIRECTOR_RELATED_PARTY(companyApplicants[0].clientId, applicantClientId),
        ]);
      }
    } else {
      setIsDirector(false);
      setFormValues(FIELD_PATHS.RELATED_PARTIES(), otherRelatedParties);
    }
  };

  const label = 'Are you a director of any company applicant?';
  const tooltip =
    'Select yes if this person applicant is the director of any company applicant. A company applicant will need to be created first.';

  const handleCompanySelection = (values: string | string[]) => {
    if (!Array.isArray(values)) return;
    setFormValues(FIELD_PATHS.RELATED_PARTIES(), [
      ...otherRelatedParties,
      ...values.map((value) => initialFormValues.INITIAL_DIRECTOR_RELATED_PARTY(value, applicantClientId)),
    ]);
  };

  return (
    <>
      <UncontrolledRadioButtonsField
        label={label}
        labelAfter={<HelpTooltip content={tooltip} />}
        radios={YES_NO_OPTIONS}
        isDisabled={!companyApplicants.length}
        direction="row"
        isOptional
        onChange={handleDirectorshipUpdate}
        selectedValue={isDirector}
      />
      {companyApplicants.length > 1 && isDirector && (
        <UncontrolledDropdownField
          label="Select company applicants"
          value={relatedDirectors.map((relatedParty) => relatedParty.companyApplicantClientId!)}
          isMultiple
          isOptional
          options={companyApplicantsOptions}
          onChange={handleCompanySelection}
        />
      )}
    </>
  );
};
